/**
 * @file SeoTitlesCard.tsx
 * @description «Сайт → SEO → Заголовки и описание» (phase19.16 Р1 → Р3в,
 * v0.19.64; SB44, SB88, SB145): заголовок главной, окончание заголовков одно
 * на магазин, описание по умолчанию и образец «Так в поиске». Перенесена из
 * конструктора: сохраняется сразу (без публикации вида сайта), у полей —
 * «изменено … · кто» и «Вернуть прежнее»; после сохранения — подсказка
 * «в поиске обновится за дни или недели» (SB145).
 */

import { SectionCard } from "../../ui/SectionCard";
import { FieldRow, FieldsWithPreview } from "../../site-view/FormLayout";
import { TextField } from "../../site-view/TextField";
import { SeoCardActions } from "./SeoCardActions";
import { SeoFieldMark } from "./SeoFieldMark";
import type { SeoCardProps } from "./types";
import { useSeoCardForm } from "../../../hooks/site-seo/useSeoCardForm";
import { SEARCH_UPDATE_NOTE } from "../../../lib/site-seo/seo-fields";
import { SEO_DESCRIPTION_SOFT_MAX, siteHost, titleWithSuffix } from "../../../lib/site-view/seo";

interface Props extends SeoCardProps {
  siteName: string;
  siteUrl: string | null;
}

const KEYS = ["titleSuffix", "defaultTitle", "description"] as const;

/** Образец строки в поиске. */
function SearchSample({ host, title, description }: { host: string; title: string; description: string | null }) {
  return (
    <div className="flex flex-col gap-1 rounded-xl bg-surface-low p-4" aria-label="Так в поиске">
      <span className="type-caption text-on-surface-variant">Так в поиске</span>
      <span className="type-caption text-on-surface-variant">{host} › catalog</span>
      <span className="type-body font-medium text-info">{title}</span>
      <span className="type-caption line-clamp-2 text-on-surface">
        {(description ?? "").slice(0, SEO_DESCRIPTION_SOFT_MAX) || "Описание не задано — поисковик возьмёт текст со страницы."}
      </span>
    </div>
  );
}

export function SeoTitlesCard({ api, settings, onHistory, readOnly, siteName, siteUrl }: Props) {
  const form = useSeoCardForm(api, KEYS);
  const { values, set } = form;
  const sample = titleWithSuffix("Каталог", values.titleSuffix);
  return (
    <SectionCard title="Заголовки и описание">
      <FieldsWithPreview preview={<SearchSample host={siteHost(siteUrl)} title={sample} description={values.description} />}>
        <FieldRow>
          <div className="flex min-w-0 flex-col gap-1">
            <TextField
              label="Окончание заголовков"
              value={values.titleSuffix}
              max={40}
              placeholder={` — ${siteName}`}
              hint={`Добавляется к каждой странице: «${sample}». Пишите с разделителем.`}
              disabled={readOnly}
              onChange={(v) => set("titleSuffix", v)}
            />
            {!values.titleSuffix?.trim() ? <p className="type-caption text-warning">Без окончания в заголовках не будет названия магазина.</p> : null}
            <SeoFieldMark field="titleSuffix" settings={settings} onHistory={onHistory} readOnly={readOnly} />
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <TextField
              label="Заголовок главной страницы"
              value={values.defaultTitle}
              max={120}
              hint="Для главной и страниц, у которых свой заголовок не заполнен."
              disabled={readOnly}
              onChange={(v) => set("defaultTitle", v)}
            />
            <SeoFieldMark field="defaultTitle" settings={settings} onHistory={onHistory} readOnly={readOnly} />
          </div>
        </FieldRow>
        <div className="flex min-w-0 flex-col gap-1">
          <TextField
            label="Описание по умолчанию"
            value={values.description}
            max={300}
            softMax={SEO_DESCRIPTION_SOFT_MAX}
            softText="Поиск покажет первые ~160 знаков, остальное обрежет."
            hint="Для страниц, у которых своё описание не заполнено."
            multiline
            disabled={readOnly}
            onChange={(v) => set("description", v)}
          />
          <SeoFieldMark field="description" settings={settings} onHistory={onHistory} readOnly={readOnly} />
        </div>
        {form.dirty ? <p className="type-caption text-on-surface-variant">{SEARCH_UPDATE_NOTE}</p> : null}
        <SeoCardActions form={form} readOnly={readOnly} />
      </FieldsWithPreview>
    </SectionCard>
  );
}
